import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isStandalone, isIosWebKit } from '../../lib/display-mode';
import { haptic } from '../../lib/haptic';

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

/**
 * Mounted on /profile only. Four states:
 *
 *   1. Already installed (display-mode standalone) → render null.
 *   2. iOS Safari + not installed                  → static hint card
 *                                                    ("Tap Share → Add
 *                                                    to Home Screen").
 *   3. Android + beforeinstallprompt fired         → Install button card.
 *   4. Android + beforeinstallprompt not yet fired → render null
 *                                                    (graceful degrade).
 */
export function InstallCard() {
  const { t } = useTranslation('common');
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  // `useState(isStandalone)` passes the fn as a lazy initializer — React calls it
  // once on first mount. Equivalent to `useState(isStandalone())` since the fn
  // takes no params; deliberate to keep the call site terse.
  const [installed, setInstalled] = useState(isStandalone);
  const [ios] = useState(isIosWebKit);

  useEffect(() => {
    if (installed) return;

    const onBeforeInstall = (e: Event) => {
      e.preventDefault();   // suppress the browser's mini-info-bar
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    const onAppInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, [installed]);

  // State 1: already installed.
  if (installed) return null;

  // State 2: iOS Safari static hint.
  if (ios) {
    return (
      <div
        className="rounded-2xl p-4 mb-4"
        style={{ background: 'var(--mint-soft)', border: '1px solid var(--mint-line)' }}
      >
        <div className="text-sm font-bold text-[var(--color-text)] mb-2">
          {t('install_card_title_ios', { defaultValue: 'Install FleetUp Driver' })}
        </div>
        <div className="text-xs text-[var(--color-text-secondary)] leading-relaxed">
          {t('install_card_hint_ios', {
            defaultValue: 'Tap the Share button below, then choose "Add to Home Screen" to install the app.',
          })}
        </div>
      </div>
    );
  }

  // State 3: Android + prompt fired.
  if (deferredPrompt) {
    return (
      <div
        className="rounded-2xl p-4 mb-4"
        style={{ background: 'var(--mint-soft)', border: '1px solid var(--mint-line)' }}
      >
        <div className="text-sm font-bold text-[var(--color-text)] mb-1">
          {t('install_card_title', { defaultValue: 'Install FleetUp Driver' })}
        </div>
        <div className="text-xs text-[var(--color-text-secondary)] mb-3">
          {t('install_card_subtitle', {
            defaultValue: 'Add to your home screen for offline access and a faster app feel.',
          })}
        </div>
        <button
          type="button"
          onClick={async () => {
            haptic.tap();
            await deferredPrompt.prompt();
            const { outcome } = await deferredPrompt.userChoice;
            if (outcome === 'accepted') setDeferredPrompt(null);
            // outcome === 'dismissed' → keep deferredPrompt; browser may re-fire later.
          }}
          className="text-sm font-semibold px-4 py-2 rounded-full"
          style={{ background: 'var(--color-btn-primary)', color: '#FFFFFF' }}
        >
          {t('install_card_cta', { defaultValue: 'Install' })}
        </button>
      </div>
    );
  }

  // State 4: pre-prompt graceful degrade.
  return null;
}
